import { Redirect } from "@shopify/app-bridge/actions";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ButtonsContainer from "src/components/RetailerSubscriptionComponents/ChooseSubscriptionParts/ButtonsContainer";
import CustomPlanCard from "src/components/RetailerSubscriptionComponents/ChooseSubscriptionParts/CustomPlanCard/CustomPlanCard";
import SubscriptionBillingIntervalSwitch from "src/components/RetailerSubscriptionComponents/ChooseSubscriptionParts/SubscriptionBillingIntervalSwitch";
import SubscriptionPlanCard from "src/components/RetailerSubscriptionComponents/ChooseSubscriptionParts/SubscriptionPlanCard";
import SubscriptionPlansContainer from "src/components/RetailerSubscriptionComponents/ChooseSubscriptionParts/SubscriptionPlansContainer";
import { useShopifyAppBridge } from "src/components/shopify/appBridge";
import {
    ERROR_MESSAGES,
    SETTINGS_MESSAGES,
    SUBSCRIPTIONS_MESSAGED,
} from "src/constants/messages";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { PLAN_TYPES } from "src/constants/subscriptionsConstants";
import Api from "src/data/api";
import { showNotification } from "src/redux/notificationActions";
import { isShopifyUserSelector } from "src/redux/selectors";
import styles from "./SettingsSubscription.module.scss";

const endpointTypes = {
    stripe: "stripe",
    shopify: "shopify",
};

const endpointsMap = {
    getAvailablePlansEndpoint: {
        shopify: "/shopify/subscriptions",
        stripe: "/stripe/prices",
    },
    updatePlanEndpoint: {
        shopify: "/retailer/subscription/shopify",
        stripe: "/retailer/subscription",
    },
    getSubscriptionInfoEndpoints: {
        shopify: "/retailer/subscription/shopify",
        stripe: "/retailer/subscription",
    },
    cancelPlanEndpoints: {
        stripe: "/retailer/subscription/cancel-subscription",
    },
    renewPlanEndpoints: {
        stripe: "/retailer/subscription/renew-subscription",
    },
};

const fetchAvailablePlans = async (endpoint, setPlans) => {
    const { data } = await Api.request({
        url: endpoint,
    });

    setPlans(data.plans);
};

const fetchCurrentSubscriptionInfo = async (
    endpoint,
    setPriceId,
    setSubscription,
    setPlanType
) => {
    const { data } = await Api.request({ url: endpoint });

    setPriceId(data.priceId);
    setSubscription(data);
    setPlanType(data.interval || PLAN_TYPES.MONTH);
};

const updateCurrentPlan = async (body, endpoint) => {
    const { data } = await Api.request({
        method: "PUT",
        data: body,
        url: endpoint,
    });

    return data;
};

const cancelCurrentPlan = async (endpoint) => {
    const { data } = await Api.request({ method: "PUT", url: endpoint });
    return data;
};

const renewCurrentPlan = async (endpoint) => {
    const { data } = await Api.request({ method: "PUT", url: endpoint });
    return data;
};

const SettingsSubscription = ({ currentNumberOfBillableLocations }) => {
    const dispatch = useDispatch();
    const isShopifyUser = useSelector(isShopifyUserSelector);
    const [subscription, setSubscription] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [plans, setPlans] = useState({ month: [], year: [] });
    const [planType, setPlanType] = useState(PLAN_TYPES.MONTH);
    const [priceId, setPriceId] = useState(null);
    const [gettingCustomerPortalLink, setGettingCustomerPortalLink] =
        useState(false);

    const { getAppBridge } = useShopifyAppBridge();

    const endpointType = isShopifyUser
        ? endpointTypes.shopify
        : endpointTypes.stripe;

    useEffect(() => {
        fetchAvailablePlans(
            endpointsMap.getAvailablePlansEndpoint[endpointType],
            setPlans
        );
        fetchCurrentSubscriptionInfo(
            endpointsMap.getSubscriptionInfoEndpoints[endpointType],
            setPriceId,
            setSubscription,
            setPlanType
        );
    }, []);

    const createCustomerPortalSession = async () => {
        setGettingCustomerPortalLink(true);
        try {
            const { data } = await Api.request({
                method: "POST",
                url: "/stripe/create-customer-portal-session",
            });
            window.location.assign(data.portalUrl);
        } catch (error) {
            console.log(error);
        }
    };

    const updateRetailerPlan = async () => {
        setIsSubmitting(true);
        const data = {
            priceId,
        };
        try {
            const result = await updateCurrentPlan(
                data,
                endpointsMap.updatePlanEndpoint[endpointType]
            );

            if (endpointType === endpointTypes.shopify && result.redirect_url) {
                const app = getAppBridge();
                Redirect.create(app).dispatch(
                    Redirect.Action.REMOTE,
                    result.redirect_url
                );
            } else {
                dispatch(
                    showNotification(
                        SUBSCRIPTIONS_MESSAGED.SUBSCRIPTION_UPDATE_SUCCESS,
                        NOTIFICATION_TYPES.SUCCESS,
                        NOTIFICATION_HEADERS.RETAILER_SUBSCRIPTION
                    )
                );
            }
        } catch (error) {
            const message =
                error.response?.data || error.request || error.message;

            dispatch(
                showNotification(
                    message,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePlanTypeChange = () => {
        setPlanType((curr) =>
            curr === PLAN_TYPES.YEAR ? PLAN_TYPES.MONTH : PLAN_TYPES.YEAR
        );
    };

    const handlePlanSelect = (plan) => {
        if (currentNumberOfBillableLocations > plan.max_stores) {
            dispatch(
                showNotification(
                    SETTINGS_MESSAGES.SELECT_PLAN_WARNING,
                    NOTIFICATION_TYPES.WARNING,
                    NOTIFICATION_HEADERS.RETAILER_SUBSCRIPTION
                )
            );
            return;
        }

        setPriceId(plan.priceId);
    };

    const cancelRetailerPlan = async () => {
        setIsSubmitting(true);

        try {
            const result = await cancelCurrentPlan(
                endpointsMap.cancelPlanEndpoints[endpointType]
            );

            setSubscription((prev) => ({
                ...prev,
                cancelAtPeriodEnd: result.cancelAtPeriodEnd,
            }));
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const renewRetailerPlan = async () => {
        setIsSubmitting(true);

        try {
            const result = await renewCurrentPlan(
                endpointsMap.renewPlanEndpoints[endpointType]
            );

            setSubscription((prev) => ({
                ...prev,
                cancelAtPeriodEnd: result.cancelAtPeriodEnd,
            }));
        } catch (error) {
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.settingsSubscriptionsPage}>
            <h1>Your plan</h1>
            <SubscriptionBillingIntervalSwitch
                planType={planType}
                handlePlanTypeChange={handlePlanTypeChange}
            />
            <SubscriptionPlansContainer className={styles.plansContainer}>
                {plans[planType].map((plan) => (
                    <SubscriptionPlanCard
                        key={plan.priceId}
                        plan={plan}
                        planType={planType}
                        priceId={priceId}
                        handlePlanSelect={() => handlePlanSelect(plan)}
                    />
                ))}
                <CustomPlanCard />
            </SubscriptionPlansContainer>
            <ButtonsContainer>
                {subscription.cancelAtPeriodEnd && (
                    <Button onClick={renewRetailerPlan} disabled={isSubmitting}>
                        Renew Plan
                    </Button>
                )}
                {(isShopifyUser || !subscription.cancelAtPeriodEnd) && (
                    <Button
                        onClick={updateRetailerPlan}
                        disabled={isSubmitting}
                    >
                        Save
                    </Button>
                )}
                {!isShopifyUser && !subscription.cancelAtPeriodEnd && (
                    <Button
                        onClick={cancelRetailerPlan}
                        disabled={isSubmitting}
                    >
                        Cancel Plan
                    </Button>
                )}
                {!isShopifyUser && (
                    <Button
                        onClick={createCustomerPortalSession}
                        disabled={gettingCustomerPortalLink}
                    >
                        {gettingCustomerPortalLink ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            "View Billing"
                        )}
                    </Button>
                )}
            </ButtonsContainer>
        </div>
    );
};

export default SettingsSubscription;
